import {inject, Injectable, Input} from "@angular/core";

import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";

import {Params} from "@angular/router";
import {IDataExportSpreadData, INavTabDataGird, ISelectOption} from '@core/models';
import {castInt, parseUrlFromParamsUri} from '@shared/utils';
import {IParamsGenerateAuthURL, IParamsLoadSpreadSheets} from '@shared/models/business';
import {ERROR_CODE} from '@core/constants';
import {AppFormAddComponent} from '@shared/components/app-form';
import {GoogleSheetService} from '@shared/services/api/business';
import {AuthService} from "@core/authentication/auth.service";

// Interface public -----------------------------------------------------
export type ITabRefExportDataSetting = 'googleSheets' | 'excel';
export type IExcelDataAbstractState = {
  tabRef: ITabRefExportDataSetting;
  businessId: number,
  accessTokenId: number | undefined; // Tab google sheet mới có đổi tab cần reset
}
export type IExcelDataAbstractOptions = {
  businessId: number,
  currentUri: string, // Link của màn hình hiện tại đang tháo để khi cấp quyền redirect trở lại
  sateParams?: Params, // Params cần truyền nối vào current uri
}


// Interface dùng trong component -----------------------------------------------------
type KEY_ERRORS = string
  | 'spreadId'
  | 'settingName';

type IProgressState = {
  currentItem: number,
  totalItems: number,
  totalPages: number,
  currentPage: number,
  spreadData?: IDataExportSpreadData,
  isFinal?: boolean,
  isError?: boolean,
  isExportSubmit?: boolean,
}

@Injectable({
  providedIn: "any"
})
export class ExcelDataAbstractService {
  activeModal: NgbActiveModal = inject(NgbActiveModal);
  appForm: AppFormAddComponent = inject(AppFormAddComponent);
  googleSheetService: GoogleSheetService = inject(GoogleSheetService);
  authService: AuthService = inject(AuthService);


  /**
   * ---------------------------------------------------------------------------------------------------------------------
   * State common ----------------------------------------------------------------------------------------------
   * ---------------------------------------------------------------------------------------------------------------------
   * */
  progressState: IProgressState = {
    currentItem: 0,
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
  }
  navTabs: INavTabDataGird[] = []
  errors: Record<KEY_ERRORS, any> = {};
  isLoadingTabGGSheet = false; // Đánh dấu đã tải xong dữ liệu tab google sheet

  // Dữ liệu cần tải từ API --------
  sheetOptions: ISelectOption[] = []; // Danh sách sheet của file đang chọn
  accessTokenOptions: ISelectOption[] = []; // Danh sách sheet của file đang chọn

  tokenError!: number; // Param đánh dấu cấp quyền thất bại
  extendParamsAuth!: Params;

  /**
   * ---------------------------------------------------------------------------------------------------------------------
   * State private for component ----------------------------------------------------------------------------------------------
   * ---------------------------------------------------------------------------------------------------------------------
   * */
    // Lưu cài đặt chọn trước khi thực hiện "xuất" | "nhập" dữ liệu
  @Input() options!: IExcelDataAbstractOptions;

  /**
   * ---------------------------------------------------------------------------------------------------------------------
   * Functions init ----------------------------------------------------------------------------------------------
   * ---------------------------------------------------------------------------------------------------------------------
   * */
  preOnLoadHandle(callbackToken: any) {
    this.tokenError = castInt(this.appForm.getParam('tokenError'));

    const tabRef = this.appForm.getParam('tabRef');
    if (tabRef == 'googleSheets') {
      this.loadDepsDataGoogleSheet(callbackToken);
    }
  }

  /**
   * ---------------------------------------------------------------------------------------------------------------------
   * Functions Handle ----------------------------------------------------------------------------------------------
   * ---------------------------------------------------------------------------------------------------------------------
   * */
  // Load dữ liệu tab "Google Sheet"
  loadDepsDataGoogleSheet(callbackToken: any) {
    this.appForm.showLoading();
    this.googleSheetService.loadAccessTokens(this.authService.businessCurrent?.id ?? 0).subscribe({
      next: (res: any) => {
        if (!res.code) {
          this.accessTokenOptions = [];
        } else {

          this.accessTokenOptions = res.data.map((tk: any) => ({
            value: tk.id,
            label: tk.email ? tk.email : tk.name,
          }))

          // Nếu chỉ có 1 tài khoản từ selected tài khoản này luôn
          if (res.data.length === 1) {
            const first = res.data.find((o: any) => o.id);
            if (first) {
              if (typeof callbackToken == 'function') {
                callbackToken(first.id);
              }
            }
          }
        }
      },
      complete: () => {
        this.appForm.hideLoading();
        this.isLoadingTabGGSheet = true;
      }
    })
  }

  /**
   * Tạo link cấp quyền
   * */
  onGenerateAuthURL() {
    if (this.appForm.disabledSubmit) return;

    const stateUri = parseUrlFromParamsUri(this.options.currentUri, {
      businessId: this.authService.businessCurrent?.id ?? 0,
      ...(this.extendParamsAuth ?? {}),
      ...(this.options.sateParams ?? {}),
    })
    const params: IParamsGenerateAuthURL = {
      businessId: this.authService.businessCurrent?.id ?? 0,
      stateUri: stateUri
    }
    this.appForm.fetchApiHandle<{ authUrl: string }>(this.googleSheetService.generateAuthURL(params), 'spiner')
      .subscribe({
        next: (res: any) => {
          if (!res.code) {
            this.appForm.notifyWarning(res.messages);
          } else {

            window.location.href = res.data.authUrl;
          }
        }
      })
  }

  // Xóa tài khoản kết nối
  onRevokeTokenGGS(opt: ISelectOption) {
    this.appForm.notifyService.dialogConfirmDelete({
      content: 'googleSheets.confirmRevokeToken', // Bạn có chắc chắn muốn gỡ kết nối
      btnConfirmName: 'common.remove.connection',
      windowClass: 'windowClassConfirmDelete',
      backdropClass: 'd-none',
    }).subscribe({
      next: (value: any) => {
        if (value && value.confirmDelete) {
          this.appForm.showLoading();
          this.googleSheetService.revokeAccessToken({
            id: opt.value,
            email: opt.label,
          }).subscribe({
            next: (res: any) => {
              if (!res.code) {
                this.appForm.notifyWarning(res.messages);
              } else {

                this.appForm.notifySuccess(res.messages);

                this.accessTokenOptions = this.accessTokenOptions.filter(
                  (o: any) => o.value != opt.value
                )
              }
            },
            complete: () => this.appForm.hideLoading()
          })
        }
      }
    })
  }

  // Lấy SpreadId trong url
  getGoogleSheetsSpreadId(url: string) {
    const regex = /https:\/\/docs\.google\.com\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  }

  /**
   * ---------------------------------------------------------------------------------------------------------------------
   * Dom Events ----------------------------------------------------------------------------------------------
   * ---------------------------------------------------------------------------------------------------------------------
   * */


  // Đổi tab excel
  onChangeTab(tab: INavTabDataGird, callbackToken: any) {
    const tRef = tab.ref as ITabRefExportDataSetting;
    if (tRef == 'googleSheets' && !this.isLoadingTabGGSheet) {
      this.loadDepsDataGoogleSheet(callbackToken);
    }
  }

  // Đổi link file Google Sheet
  onChangeSpreadGGUrl(spreadUrl: string, accessTokenId: number | undefined) {
    delete this.errors['spreadId'];
    this.onCheckSpread(spreadUrl, accessTokenId);
  }

  /**
   * Kiểm tra spread có hợp lệ không và load danh sách sheets
   * */
  onCheckSpread(url: string, accessTokenId: number | undefined) {
    const spreadId = this.getGoogleSheetsSpreadId(url);
    if (!spreadId || !accessTokenId) {
      this.errors['spreadId'] = 'Link file Google Sheets không hợp lệ';
      return;
    }
    delete this.errors['spreadId'];

    const params: IParamsLoadSpreadSheets = {
      businessId: this.authService.businessCurrent?.id ?? 0,
      spreadId: spreadId,
      accessTokenId: castInt(accessTokenId)
    }

    this.appForm.showLoading();
    this.appForm.disabledSubmit = true;
    this.googleSheetService.checkSpreadId(params).subscribe({
      next: (res: any) => {
        if (!res.code) {
          if (res.errorCode == ERROR_CODE.ERR_INVALID_FORM_FIELDS && res.messages['spreadId']) {
            this.errors['spreadId'] = res.messages['spreadId'];
          } else {

            this.appForm.notifyWarning(res.messages as any);
          }
        } else {
          this.sheetOptions = res.data.sheets;
        }
      },
      complete: () => {
        this.appForm.hideLoading();
        this.appForm.disabledSubmit = false;
      }
    })
  }
}
